import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Groups as TotalIcon,
  CheckCircle as AccountedIcon,
  Cancel as NotAccountedIcon,
  FileDownload as ExportIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { IncidentSummary, Incident } from '../Types/Incident';
import { getIncidentSummary, getIncidentDetail, closeIncident } from '../Services/IncidentService';
import { appColors } from '../Config/Theme';
import { isIncidentClosed, canEditClosedRoster, INCIDENT_EDIT_ROLES } from '../Utils/incidentHelpers';
import {
  getDashboardCounts,
  getIncidentDashboardState,
  getPrimaryDashboardAction,
  getProgressBarColor,
  getProgressLabel,
  getMetricCardColor,
} from '../Utils/incidentDashboardState';
import IncidentMetricStrip from '../Components/Shared/IncidentMetricStrip';
import IncidentNextActionPanel from '../Components/Dashboard/IncidentNextActionPanel';
import AccountabilityByViewPanel from '../Components/Dashboard/AccountabilityByViewPanel';
import RecentActivityPanel from '../Components/Dashboard/RecentActivityPanel';
import HeadcounterActivityPanel from '../Components/Dashboard/HeadcounterActivityPanel';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import HeadcountTeamSummary from '../Components/Incidents/HeadcountTeamSummary';
import IncidentTeamSummary from '../Components/Incidents/IncidentTeamSummary';
import IncidentTeamDrawer from '../Components/Incidents/IncidentTeamDrawer';
import CloseIncidentDialog from '../Components/Incidents/CloseIncidentDialog';
import { formatTimeWithSeconds } from '../Utils/dateHelpers';
import { useOnlineStatus } from '../Hooks/useOnlineStatus';
import SyncStatusIndicator from '../Components/Elements/SyncStatusIndicator';
import { useIncidentRealtime } from '../Hooks/useIncidentRealtime';
import type {
  RosterPersonStatusChangedEvent,
  IncidentClosedEvent,
  IncidentRefreshRequestedEvent,
} from '../realtime/incidentRealtimeTypes';
import { isValidHeadcountStatus } from '../realtime/incidentRealtimeHelpers';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import { deriveScopeCountsFromSummary } from '../features/incidents/accountabilityScope';
import { useSecurity } from '../Contexts/SecurityProvider';

const AUTO_REFRESH_MS = 30_000;
const AUTO_REFRESH_REALTIME_MS = 120_000;

const IncidentDashboardPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline } = useOnlineStatus();
  const { hasAnyRole, security } = useSecurity();
  const currentIncidentId = useRef(incidentId);
  const fetchGeneration = useRef(0);
  currentIncidentId.current = incidentId;
  useEffect(() => {
    currentIncidentId.current = incidentId;
    return () => {
      if (currentIncidentId.current === incidentId) currentIncidentId.current = undefined;
    };
  }, [incidentId]);

  const [summary, setSummary] = useState<IncidentSummary | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activityRefreshTrigger, setActivityRefreshTrigger] = useState(0);

  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [teamDrawerOpen, setTeamDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  useEffect(() => { setCloseDialogOpen(false); }, [incidentId]);

  // Show snackbar from route state (e.g. after editing incident details)
  useEffect(() => {
    const state = location.state as { snackbar?: string } | null;
    if (state?.snackbar) {
      setSnackbar({ open: true, message: state.snackbar, severity: 'success' });
      // Clear state so it doesn't re-trigger on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Incident team assignments
  const {
    assignments: teamAssignments,
    loading: teamLoading,
    error: teamError,
  } = useIncidentAssignments(incidentId);

  const fetchData = useCallback(async (showSpinner: boolean) => {
    if (!incidentId) return;
    const requestGeneration = ++fetchGeneration.current;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [sumResult, detailResult] = await Promise.all([
        getIncidentSummary(incidentId),
        getIncidentDetail(incidentId).catch(() => null),
      ]);
      if (currentIncidentId.current !== incidentId || requestGeneration !== fetchGeneration.current) return;
      setSummary(sumResult.summary);
      setIncident(detailResult);
      setActivityRefreshTrigger((prev) => prev + 1);
      const serverTime = sumResult.serverTimeUtc;
      if (serverTime) {
        setLastUpdated(formatTimeWithSeconds(serverTime));
      } else {
        setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }));
      }
    } catch (err: unknown) {
      if (currentIncidentId.current !== incidentId || requestGeneration !== fetchGeneration.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data.');
    } finally {
      if (currentIncidentId.current === incidentId && requestGeneration === fetchGeneration.current) setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    fetchData(true);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData]);

  // ── Realtime SignalR ──
  const dashboardIsClosed = isIncidentClosed(incident) || isIncidentClosed(summary);

  const handleRealtimeStatusChanged = useCallback((event: RosterPersonStatusChangedEvent) => {
    if (!isValidHeadcountStatus(event.effectiveHeadcountStatus)) return;

    // Update summary counts from event (authoritative)
    if (event.counts) {
      setSummary((prev) => {
        if (!prev) return prev;

        const percentComplete = event.counts.total > 0
          ? Math.round((event.counts.accounted / event.counts.total) * 100)
          : 0;

        // When scopeCounts exist, getDashboardCounts reads from them instead of
        // the flat fields.  Keep scopeCounts in sync so the KPI cards update
        // immediately via the realtime event rather than waiting for a full
        // API re-fetch.
        //
        // IMPORTANT: preserve the original accountabilityCounts.total — it was
        // computed from person-level scope data by the API and must not be
        // replaced with (fullRosterTotal − noBadge) which is an approximation.
        // Only the status-breakdown fields change when someone is marked.
        let updatedScopeCounts = prev.scopeCounts;
        if (updatedScopeCounts) {
          const prevAccountabilityTotal = updatedScopeCounts.accountabilityCounts.total;
          const accountabilityNotChecked = Math.max(0, prevAccountabilityTotal - event.counts.accounted - event.counts.notAccounted);
          const accountabilityPercent = prevAccountabilityTotal === 0
            ? 0
            : Math.round((event.counts.accounted / prevAccountabilityTotal) * 100);

          updatedScopeCounts = {
            ...updatedScopeCounts,
            fullRosterCounts: {
              total: event.counts.total,
              accounted: event.counts.accounted,
              notAccounted: event.counts.notAccounted,
              notChecked: event.counts.notChecked,
              percentComplete,
            },
            accountabilityCounts: {
              ...updatedScopeCounts.accountabilityCounts,
              accounted: event.counts.accounted,
              notAccounted: event.counts.notAccounted,
              notChecked: accountabilityNotChecked,
              percentComplete: accountabilityPercent,
            },
            needsAttentionCount: event.counts.notAccounted,
          };
        }

        return {
          ...prev,
          total: event.counts.total,
          accounted: event.counts.accounted,
          notAccounted: event.counts.notAccounted,
          notChecked: event.counts.notChecked,
          percentComplete,
          ...(updatedScopeCounts ? { scopeCounts: updatedScopeCounts } : {}),
        };
      });
    }

    setLastUpdated(formatTimeWithSeconds(event.lastUpdatedAtUtc));
    setActivityRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleRealtimeIncidentClosed = useCallback((event: IncidentClosedEvent) => {
    if (currentIncidentId.current !== event.incidentId) return;
    // Update both summary and incident to reflect closed status
    setSummary((prev) => prev ? { ...prev, status: 'closed', closedAtUtc: event.closedAtUtc } : prev);
    setIncident((prev) => prev ? { ...prev, status: 'closed', closedAtUtc: event.closedAtUtc } : prev);
    setSnackbar({
      open: true,
      message: event.closedByDisplayName
        ? `Incident closed by ${event.closedByDisplayName}`
        : 'Incident has been closed',
      severity: 'info',
    });
    // Closing may also set the previously unselected type; retrieve that authoritative value.
    void fetchData(false);
  }, [fetchData]);

  const handleRealtimeRefreshRequested = useCallback((_event: IncidentRefreshRequestedEvent) => {
    fetchData(false);
  }, [fetchData]);

  const handleRealtimeReconnect = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  const { connectionState: realtimeState } = useIncidentRealtime({
    incidentId,
    enabled: !loading && !dashboardIsClosed,
    onRosterPersonStatusChanged: handleRealtimeStatusChanged,
    onIncidentClosed: handleRealtimeIncidentClosed,
    onIncidentRefreshRequested: handleRealtimeRefreshRequested,
    onReconnect: handleRealtimeReconnect,
  });

  const realtimeConnected = realtimeState === 'connected';

  // ── Auto-refresh polling (slower when realtime is active) ──
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!dashboardIsClosed) {
      const interval = realtimeConnected ? AUTO_REFRESH_REALTIME_MS : AUTO_REFRESH_MS;
      timerRef.current = setInterval(() => fetchData(false), interval);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.status, summary?.status, fetchData, realtimeConnected]);

  const handleCloseIncident = async (notes: string, incidentType?: string) => {
    if (!incidentId) return;
    await closeIncident(incidentId, { notes: notes || undefined, ...(incidentType ? { incidentType } : {}) });
    if (currentIncidentId.current !== incidentId) return;
    setCloseDialogOpen(false);
    setSnackbar({ open: true, message: 'Incident closed successfully.', severity: 'success' });
    await fetchData(true);
  };

  const handleExportReport = () => {
    if (!summary || !incidentId || !counts) return;
    const name = summary.incidentName || incident?.incidentName || 'incident';
    const rows = [
      ['Incident Report'],
      ['Name', name],
      ['Location', summary.location || 'N/A'],
      ['Status', isIncidentClosed({ status: summary.status }) ? 'Closed' : 'Active'],
      ['Started', summary.startedAtUtc || ''],
      [],
      ['Accountability Summary (Accountability Roster)'],
      ['Accountability Total', String(counts.total)],
      ['Accounted', String(counts.accounted)],
      ['Not Accounted', String(counts.needsAttentionCount)],
      ['Pending Review (Not Checked)', String(counts.notChecked)],
      ['Percent Complete', `${Math.round(counts.accountabilityPercent)}%`],
      [],
      ['Full Roster Summary'],
      ['Full Roster Total', String(counts.fullRosterTotal)],
      ['Visitors', String(counts.visitorCount)],
      ['Assigned Office / No Badge Today', String(counts.assignedOfficeNoBadge)],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^a-zA-Z0-9]/g, '_')}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Derived state ──
  const counts = useMemo(() => summary ? getDashboardCounts(summary) : null, [summary]);
  const displayStatus = summary?.status ?? incident?.status;
  const dashboardState = useMemo(
    () => counts ? getIncidentDashboardState(counts, displayStatus as string | undefined) : 'notStarted' as const,
    [counts, displayStatus],
  );
  const isClosed = dashboardState === 'closed';
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments: teamAssignments,
  });

  const canEditDetails = incident?.capabilities?.canEditDetails === true
    || hasAnyRole(INCIDENT_EDIT_ROLES);



  const closeDialogCounts = useMemo(
    () => summary ? deriveScopeCountsFromSummary(summary).accountabilityCounts : null,
    [summary],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchData(true)}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!summary || !counts) return null;

  const displayName = summary.incidentName || incident?.incidentName || '';
  const displayLocation = summary.location || incident?.location || '';
  const displayType = summary.incidentType !== undefined ? summary.incidentType : incident?.incidentType;
  const displayStarted = summary.startedAtUtc || incident?.startedAtUtc || '';

  const hasTeam = summary.incidentCommanders.length > 0 || summary.headcounters.length > 0;
  const hasHeadcounterActivity = summary.headcounterActivity.length > 0;
  const hasViewData = summary.accountabilityByView.length > 0;

  const primaryAction = getPrimaryDashboardAction(dashboardState, incidentId!);
  const progressColor = getProgressBarColor(dashboardState, counts.accountabilityPercent);
  const progressLabel = getProgressLabel(dashboardState, counts);

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: displayName || 'Incident Dashboard' },
      ]} />

      {/* ── Closed banner ── */}
      {isClosed && !closedRosterEditable && <ClosedIncidentBanner closedAtUtc={incident?.closedAtUtc} />}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Incident Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1, lg: 2 }, flexWrap: 'wrap' }}>
          {!isClosed && (
            <SyncStatusIndicator
              state={isOnline ? 'online' : 'offline'}
              lastSyncedAt={lastUpdated}
            />
          )}
          {lastUpdated && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
              Last updated: {lastUpdated}
            </Typography>
          )}
          {/* Primary header action — state-aware */}
          <Button
            variant={primaryAction.variant}
            color={primaryAction.color}
            size="small"
            onClick={() => {
              if (dashboardState === 'complete') setCloseDialogOpen(true);
              else if (primaryAction.route) navigate(primaryAction.route);
              else fetchData(true);
            }}
            sx={{ fontWeight: 700 }}
          >
            {primaryAction.label}
          </Button>
          {canEditDetails && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/incidents/${incidentId}/edit`)}
              sx={{ fontWeight: 600 }}
            >
              Edit Details
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExportIcon />}
            onClick={handleExportReport}
            sx={{
              borderColor: appColors.primary.main,
              color: appColors.primary.main,
              fontWeight: 600,
              '&:hover': { borderColor: appColors.primary.main, bgcolor: `${appColors.primary.main}08` },
            }}
          >
            Export Report
          </Button>
          {!isClosed && dashboardState !== 'complete' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => setCloseDialogOpen(true)}
              disabled={!isOnline}
            >
              Close Incident
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Incident Info ── */}
      <IncidentInfoBar
        incidentName={displayName}
        incidentType={displayType}
        location={displayLocation}
        startedAtUtc={displayStarted}
        status={displayStatus}
      />

      {/* ── Incident Team Summary Strip ── */}
      <Box sx={{ mb: 1 }}>
        <IncidentTeamSummary
          assignments={teamAssignments}
          loading={teamLoading}
          error={teamError}
          onViewTeam={() => setTeamDrawerOpen(true)}
        />
      </Box>



      {/* ── Next Action Panel ── */}
      <IncidentNextActionPanel
        state={dashboardState}
        counts={counts}
        incidentId={incidentId!}
        onNavigate={navigate}
        onCloseIncident={() => setCloseDialogOpen(true)}
        onRefresh={() => fetchData(true)}
        onExport={handleExportReport}
      />

      {/* ── Primary Metric Cards ── */}
      <Box sx={{ mb: 2.5 }}>
        <IncidentMetricStrip
          metrics={[
            {
              key: 'toAccount',
              icon: <TotalIcon />,
              label: 'To Account',
              value: counts.total,
              description: 'Accountability roster',
              variant: 'toAccount',
              onClick: () => navigate(`/incidents/${incidentId}/headcount`),
              ariaLabel: `View all ${counts.total} accountability total`,
            },
            {
              key: 'accounted',
              icon: <AccountedIcon />,
              label: 'Accounted',
              value: counts.accounted,
              description: counts.total > 0 ? `${Math.round(counts.accountabilityPercent)}% of accountability roster` : 'People accounted',
              variant: 'accounted',
              onClick: () => navigate(`/incidents/${incidentId}/headcount?status=accounted`),
              ariaLabel: `View ${counts.accounted} accounted people`,
            },
            {
              key: 'notAccounted',
              icon: <NotAccountedIcon />,
              label: 'Not Accounted',
              value: counts.needsAttentionCount,
              description: 'People not accounted',
              variant: 'notAccounted',
              neutralWhenZero: true,
              onClick: () => navigate(`/incidents/${incidentId}/unaccounted`),
              ariaLabel: `View ${counts.needsAttentionCount} people not accounted`,
            },
          ]}
          contextMetrics={[
            {
              key: 'fullRoster',
              label: 'Full Roster',
              value: counts.fullRosterTotal,
              ariaLabel: `Full Roster, ${counts.fullRosterTotal} people`,
            },
            {
              key: 'noBadgeToday',
              label: 'No Badge Today',
              value: counts.assignedOfficeNoBadge,
              ariaLabel: `No Badge Today, ${counts.assignedOfficeNoBadge} people`,
            },
          ]}
        />
      </Box>

      {/* ── Unsynced warning (only when count > 0) ── */}
      {counts.unsyncedChanges > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2.5, borderRadius: '12px' }}
        >
          {counts.unsyncedChanges} {counts.unsyncedChanges === 1 ? 'change' : 'changes'} not synced
        </Alert>
      )}

      {/* ── Accountability Progress ── */}
      <Box sx={{ bgcolor: appColors.surface.card, borderRadius: '12px', border: `1px solid ${appColors.border.default}`, px: 3, py: 2, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Accountability Progress
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: progressColor }}>
            {Math.round(counts.accountabilityPercent)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={counts.accountabilityPercent}
          sx={{
            height: 12,
            borderRadius: 6,
            bgcolor: appColors.border.default,
            '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 6 },
          }}
        />
        <Typography variant="caption" sx={{ color: appColors.text.placeholder, mt: 0.5, display: 'block', textAlign: 'right' }}>
          {progressLabel}
        </Typography>
      </Box>

      {/* ── Activity panels ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        {hasViewData && <AccountabilityByViewPanel views={summary.accountabilityByView} />}
        {hasHeadcounterActivity && <HeadcounterActivityPanel headcounters={summary.headcounterActivity} />}
      </Box>

      {/* ── Recent Activity (self-fetching paginated panel) ── */}
      <Box sx={{ mb: 2.5 }}>
        <RecentActivityPanel incidentId={incidentId!} refreshTrigger={activityRefreshTrigger} />
      </Box>

      {/* ── Accountability by View empty-state ── */}
      {!hasViewData && (
        <Box
          sx={{
            bgcolor: appColors.surface.card,
            borderRadius: '12px',
            border: `1px solid ${appColors.border.default}`,
            px: 3,
            py: 2,
            mb: 2.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1 }}>
            Accountability by View
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.caption, mb: 1 }}>
            Roster views are available dynamically. Track progress by full roster, floor, department, person type, or last-name range.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/incidents/${incidentId}/views`)}
            sx={{ fontWeight: 600 }}
          >
            Open Roster Views
          </Button>
        </Box>
      )}

      {/* ── Incident Team Drawer ── */}
      <IncidentTeamDrawer
        open={teamDrawerOpen}
        onClose={() => setTeamDrawerOpen(false)}
        assignments={teamAssignments}
        loading={teamLoading}
        error={teamError}
        incidentName={displayName}
        incidentId={incidentId}
      />

      {/* ── Close Incident Dialog ── */}
      <CloseIncidentDialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        onConfirm={handleCloseIncident}
        incidentId={incidentId!}
        incidentName={displayName}
        incidentType={displayType}
        total={closeDialogCounts?.total ?? 0}
        accounted={closeDialogCounts?.accounted ?? 0}
        notAccounted={closeDialogCounts?.notAccounted ?? 0}
        notChecked={closeDialogCounts?.notChecked ?? 0}
        percentComplete={closeDialogCounts?.percentComplete ?? 0}
        unsyncedChanges={summary.unsyncedChanges}
        isClosed={isClosed}
      />



      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentDashboardPage;
